/**
 * API client for backend communication
 */
import type { CompletenessResult } from '@/types/completeness';
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import type {
  LoginRequest,
  TokenResponse,
  UserInfo,
  ConfigVersion,
  ConfigComponent,
  RateLimiter,
  HealthCheck,
  SystemMetrics,
} from '@/types';
import type { MicroClassListItem } from '@/types/micro-class-builder';

// Create axios instance
// ── Shared axios setup (consolidation A, 2026-06-13) ──────────────
//
// One source of truth for:
//   - the API_BASE_URL prefix (Fuad's 2026-06-12 catch — `apiBase.ts`
//     alone didn't help since the main axios layer here used relative
//     baseURLs, which browsers resolved against the CC's own origin
//     instead of the API container in Docker)
//   - auth header injection
//   - 401 redirect with once-only guard
//
// New rule: every axios instance in this file is created via
// `createScopedAxios('/api/foo')`. Never call `axios.create(...)`
// directly here. That guarantees:
//   (a) baseURL is prefixed with API_BASE_URL (works in Docker)
//   (b) interceptors are applied (works after auth expires)
//
// Future drift caught at review.

import { API_BASE_URL } from '@/lib/apiBase';

let _has401Bounced = false;

// 2026-06-14 (Nino "Ugly" sweep): track the user's last meaningful
// interaction (click, keypress, focus). The 401 interceptor uses this
// to decide whether a 401 belongs to a foreground action (redirect to
// /login is the right call) or a background poll (silent fail — don't
// boot the user mid-task).
//
// The 22+ polling queries across CC all use `api` (the foreground
// instance). Without this, any of them can force-logout on token
// expiry. Single source of truth is much safer than patching call
// sites one-by-one.
const USER_INTERACTION_WINDOW_MS = 10_000;
let _lastInteractionAt = Date.now();

if (typeof window !== 'undefined') {
  const tick = () => { _lastInteractionAt = Date.now(); };
  // 'mousedown' covers clicks, 'keydown' covers typing, 'visibilitychange'
  // covers tab focus. Passive listeners so we don't block anything.
  window.addEventListener('mousedown', tick, { passive: true });
  window.addEventListener('keydown', tick, { passive: true });
  window.addEventListener('focus', tick, { passive: true });
}

interface InterceptorOpts {
  skip401Redirect?: boolean;
}

function applyStandardInterceptors(
  instance: AxiosInstance,
  opts: InterceptorOpts = {},
): AxiosInstance {
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem('access_token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (
        !opts.skip401Redirect &&
        error.response?.status === 401 &&
        !_has401Bounced
      ) {
        // 2026-06-14: only redirect when the failing request was tied
        // to recent user interaction. Background polls (React Query
        // refetchInterval) silently fail; React Query will retry on the
        // next user-triggered fetch and 401 again, at which point we
        // bounce. This stops the "I was idle for 30s and got logged
        // out" pattern that sidebar-counters surfaced.
        const sinceInteraction = Date.now() - _lastInteractionAt;
        if (sinceInteraction <= USER_INTERACTION_WINDOW_MS) {
          _has401Bounced = true;
          try { localStorage.removeItem('access_token'); } catch { /* noop */ }
          window.location.replace('/login');
        } else {
          // Silent fail — don't redirect, just log.
          console.warn(
            '[api] 401 on background request (no interaction in last',
            Math.round(sinceInteraction / 1000) + 's). Not redirecting.',
          );
        }
      }
      return Promise.reject(error);
    },
  );
  return instance;
}

/**
 * Create + scope an axios instance for a specific path prefix.
 * The prefix is prepended with `API_BASE_URL` so calls land on the
 * right host both in dev (proxied via Vite) and Docker (different
 * container, full URL via VITE_API_BASE_URL).
 *
 * Example: createScopedAxios('/api/v1') → baseURL is either
 *   /api/v1                          (dev, VITE_API_BASE_URL unset)
 *   http://playbook-api:8000/api/v1  (Docker, VITE_API_BASE_URL set)
 */
function createScopedAxios(prefix: string, opts: InterceptorOpts = {}): AxiosInstance {
  return applyStandardInterceptors(
    axios.create({
      baseURL: `${API_BASE_URL}${prefix}`,
      headers: { 'Content-Type': 'application/json' },
    }),
    opts,
  );
}

const api: AxiosInstance = createScopedAxios('/api/v1');

// 2026-06-13: dedicated axios for non-critical polling endpoints (sidebar
// counters, etc.). The standard 401 interceptor force-redirects to /login,
// which is correct for primary API calls but catastrophic for a 60s poll —
// one expired-token tick = surprise logout. This client swallows 401s
// silently so a polling failure never logs the user out.
const apiPolling: AxiosInstance = createScopedAxios('/api/v1', { skip401Redirect: true });

// Authentication API
export const authApi = {
  login: (credentials: LoginRequest) =>
    api.post<TokenResponse>('/auth/login', credentials),

  logout: () => api.post('/auth/logout'),

  getCurrentUser: () => api.get<UserInfo>('/auth/me'),

  refreshToken: () => api.post<TokenResponse>('/auth/refresh'),
};


// Configuration API
export const configApi = {
  listVersions: (params: { component?: string; limit?: number }) =>
    api.get<{ versions: ConfigVersion[]; total: number }>('/config/versions', {
      params,
    }),

  getVersion: (versionId: string) =>
    api.get<ConfigVersion>(`/config/versions/${versionId}`),

  getActive: (component?: string) =>
    api.get<{ active_configs: ConfigVersion[]; total: number }>(
      '/config/active',
      { params: { component } }
    ),

  activateVersion: (versionId: string) =>
    api.post(`/config/versions/${versionId}/activate`),

  compareVersions: (versionId: string, compareVersionId: string) =>
    api.get(`/config/versions/${versionId}/diff/${compareVersionId}`),

  listComponents: () =>
    api.get<{ components: ConfigComponent[]; total: number }>(
      '/config/components'
    ),
};



// Rate Limiters API
export const rateLimitersApi = {
  list: () =>
    api.get<{ limiters: RateLimiter[]; total: number }>('/rate-limiters'),

  get: (limiterId: string) =>
    api.get<RateLimiter>(`/rate-limiters/${limiterId}`),

  reset: (limiterId: string) =>
    api.post(`/rate-limiters/${limiterId}/reset`),
};

// System API
export const systemApi = {
  health: () => api.get<HealthCheck>('/system/health'),

  info: () => api.get('/system/info'),

  metrics: () => api.get<SystemMetrics>('/system/metrics'),

  tables: () => api.get('/system/database/tables'),
};


// ── Scheduler API ─────────────────────────────────────────────────────
// Uses /api/scheduler prefix (separate from /api/v1).
// Consolidation A (2026-06-13) — shares the standard interceptors.
// The old FP-path-check redirect skip is dead now that FP is a
// separate SPA at /facilitator/ (different window). Standard
// behavior is fine.
const schedulerApi: AxiosInstance = createScopedAxios('/api/scheduler');

export const schedulerApiClient = {
  // Facilitators
  listFacilitators: async (status?: string) => {
    const response = await schedulerApi.get('/facilitators', { params: status ? { status } : {} });
    return response.data;
  },
  createFacilitator: async (data: {
    display_name: string;
    email: string;
    timezone?: string;
    qualified_workshops?: string[];
    max_sessions_per_day?: number;
    max_sessions_per_week?: number;
  }) => {
    const response = await schedulerApi.post('/facilitators', data);
    return response.data;
  },
  updateFacilitator: async (facilitatorId: string, data: Record<string, any>) => {
    const response = await schedulerApi.patch(`/facilitators/${facilitatorId}`, data);
    return response.data;
  },

  // Sessions
  listSessions: async (params: {
    status?: string;
    facilitator_id?: string;
    from_date?: string;
    to_date?: string;
  }) => {
    const response = await schedulerApi.get('/sessions', { params });
    return response.data;
  },
  createSession: async (data: {
    workshop_id: string;
    workshop_title: string;
    short_title?: string;
    facilitator_id?: string;
    scheduled_start: string;
    duration_minutes: number;
    max_participants?: number;
    notes?: string;
  }) => {
    const response = await schedulerApi.post('/sessions', data);
    return response.data;
  },
  updateSession: async (sessionId: string, data: Record<string, any>) => {
    const response = await schedulerApi.patch(`/sessions/${sessionId}`, data);
    return response.data;
  },
  cancelSession: async (sessionId: string) => {
    const response = await schedulerApi.post(`/sessions/${sessionId}/cancel`);
    return response.data;
  },
  startSession: async (sessionId: string, data?: { mode?: string; role_tier?: string }) => {
    const response = await schedulerApi.post(`/sessions/${sessionId}/start`, data || {});
    return response.data;
  },

  // Bookings
  getSessionBookings: async (sessionId: string) => {
    const response = await schedulerApi.get(`/sessions/${sessionId}/bookings`);
    return response.data;
  },
  bookSession: async (sessionId: string, data: { learner_id: string; role_assignment?: string }) => {
    const response = await schedulerApi.post(`/sessions/${sessionId}/book`, data);
    return response.data;
  },
  cancelBooking: async (bookingId: string) => {
    const response = await schedulerApi.delete(`/bookings/${bookingId}`);
    return response.data;
  },

  // Individual facilitator
  getFacilitator: async (facilitatorId: string) => {
    const response = await schedulerApi.get(`/facilitators/${facilitatorId}`);
    return response.data;
  },
  approveFacilitator: async (facilitatorId: string) => {
    const response = await schedulerApi.post(`/facilitators/${facilitatorId}/approve`);
    return response.data;
  },
  rejectFacilitator: async (facilitatorId: string, reason: string) => {
    const response = await schedulerApi.post(`/facilitators/${facilitatorId}/reject`, { reason });
    return response.data;
  },
  deactivateFacilitator: async (facilitatorId: string) => {
    const response = await schedulerApi.post(`/facilitators/${facilitatorId}/deactivate`);
    return response.data;
  },
  getAvailability: async (facilitatorId: string) => {
    // Backend returns {facilitator_id, slots: [...]}. Unwrap so callers
    // get a plain array, matching the shape AvailabilityTab.loadAvailability
    // and any future scheduler UI naturally expects.
    const response = await schedulerApi.get(`/facilitators/${facilitatorId}/availability`);
    return response.data?.slots ?? [];
  },

  // Catalogue
  getCatalogue: async (params?: { from_date?: string; to_date?: string; workshop_title?: string }) => {
    const response = await schedulerApi.get('/catalogue', { params });
    return response.data;
  },

  // ── Facilitator Portal endpoints ───────────────────────────────────
  loginFacilitator: async (data: { email: string; password: string }) => {
    const response = await schedulerApi.post('/facilitators/login', data);
    return response.data;
  },
  registerFacilitator: async (data: {
    display_name: string;
    email: string;
    password: string;
    timezone: string;
    qualified_workshops?: string[];
    max_sessions_per_day?: number;
    max_sessions_per_week?: number;
    profile_data?: Record<string, unknown>;
  }) => {
    const response = await schedulerApi.post('/facilitators/register', data);
    return response.data;
  },
  setAvailability: async (facilitatorId: string, slots: Array<Record<string, unknown>>) => {
    const response = await schedulerApi.put(`/facilitators/${facilitatorId}/availability`, { slots });
    return response.data;
  },
  getProposals: async () => {
    const response = await schedulerApi.get('/proposals');
    return response.data;
  },
  respondToProposal: async (proposalId: string, action: string, reason?: string) => {
    const response = await schedulerApi.post(`/proposals/${proposalId}/respond`, {
      action,
      reason: reason || null,
    });
    return response.data;
  },
  getSessionWorkshopContent: async (sessionId: string) => {
    const response = await schedulerApi.get(`/sessions/${sessionId}/workshop-content`);
    return response.data;
  },
  /** Returns the raw, unflattened published workshop JSON by
   *  workshop_id. Used by the FP WorkshopConsole to render the full
   *  v2 shape (hiddenContext, redLines, successCriteria, scene
   *  evidenceContract). Skip the session detour — bridge sessions
   *  carry workshop_id directly. */
  getWorkshopRawContent: async (workshopId: string) => {
    const response = await schedulerApi.get(`/workshops/${workshopId}/raw-content`);
    return response.data;
  },
  facilitatorWithdraw: async (sessionId: string) => {
    const response = await schedulerApi.post(`/sessions/${sessionId}/facilitator-withdraw`);
    return response.data;
  },
};

// ── Bridge API ───────────────────────────────────────────────────────
// Uses /api/bridge prefix (separate from /api/v1).
// Consolidation A (2026-06-13) — shares the standard interceptors.
const bridgeApi: AxiosInstance = createScopedAxios('/api/bridge');

/** One drill in GET /api/bridge/sessions/active (admin): every session not yet ended. */
export interface ActiveDrillSummary {
  session_id: string;
  join_code: string;
  workshop_id: string;
  workshop_title: string;
  mode: 'online' | 'onsite' | 'hybrid' | string;
  phase: 'setup' | 'act1' | 'twist' | 'act2' | 'debrief' | string;
  participant_count: number;
  connected_count: number;
  elapsed_seconds: number;
  remaining_seconds: number;
  twist_count: number;
  roles_released: boolean;
  last_activity: string;
  // Asked of Scoring for "Live drills" (6 Oct); absent until they ship.
  facilitator?: { id: string; name: string } | null;
  alerts?: { count: number } | null;
  observer?: { id?: string; name: string } | null;
  /** Everyone observing (Scoring, 7 Oct). */
  observers?: Array<{ id: string; name: string }>;
  /** Whether the facilitator's Studio is connected; false while they're away. */
  facilitator_present?: boolean;
  /** In the 5-minute wait after the facilitator dropped; the drill ends if they don't return. */
  facilitator_waiting?: boolean;
  /** The booking's planned start (UTC), for 'Lobby · starts in N min'. Asked of Scoring, 6 Oct. */
  scheduled_start?: string | null;
  // Live Drills' additions for the Operations page (answers 1–7, 17 Oct); absent until they ship.
  /** Alerts in words, each with when it began (replaces the bare count). */
  alert_list?: Array<{ key?: string; code: string; name?: string | null; since?: string | null; words: string }> | null;
  /** One line per learner: never_joined | lobby | in | dropped, since when, and whether they're recorded. The first name can be null; learner_id is the account (null for a walk-in). */
  learners?: Array<{ participant_id?: string; learner_id?: string | null; name: string | null; role?: string | null; state: 'never_joined' | 'lobby' | 'in' | 'dropped'; since?: string | null; recording?: boolean | null; silent_since?: string | null }> | null;
  recording?: { on: boolean; learners_recorded?: number; learners_expected?: number } | null;
  venue?: { name: string | null; mode: string; confirmed?: boolean | null } | null;
  facilitator_away_since?: string | null;
  facilitator_wait_until?: string | null;
  /** Handled problems, stored on the server so every staff member sees them (asked 18 Oct). */
  handled?: Array<{ key: string; by: string; at: string; note?: string | null }> | null;
}

export const bridgeApiClient = {
  // Sessions
  getActiveSessions: async (): Promise<{ count: number; sessions: ActiveDrillSummary[] }> => {
    const response = await bridgeApi.get('/sessions/active');
    return response.data;
  },
  /** Operations' "Mark handled" (Live Drills 4aeb0783): staff only, the first press is kept, so every staff screen sees who and when. */
  markHandled: async (sessionId: string, key: string): Promise<{ key: string; by: string; at: string; note?: string | null }> => {
    const response = await bridgeApi.post(`/sessions/${encodeURIComponent(sessionId)}/handled`, { key: key.slice(0, 80) });
    return response.data;
  },
  getSession: async (sessionId: string) => {
    const response = await bridgeApi.get(`/session/${sessionId}`);
    return response.data;
  },
  getSessionByCode: async (joinCode: string) => {
    const response = await bridgeApi.get(`/session/by-code/${joinCode}`);
    return response.data;
  },
  createSession: async (data: {
    workshop_id: string;
    workshop_title?: string;
    facilitator_id?: string;
    mode?: string;
    duration_minutes?: number;
    role_tier?: string;
    scenario_text?: string;
    context_text?: string;
    available_twists?: Array<Record<string, any>>;
    /** Optional — when launching from a published ScheduledSession,
     *  pass its session_id so the backend pre-seeds participants
     *  from confirmed bookings. Without this, web join-by-code
     *  rejects every learner with "roster doesn't include you". */
    scheduled_session_id?: string;
  }) => {
    const response = await bridgeApi.post('/session', data);
    return response.data;
  },
  startSession: async (sessionId: string) => {
    const response = await bridgeApi.post(`/session/${sessionId}/start`);
    return response.data;
  },
  endSession: async (sessionId: string) => {
    const response = await bridgeApi.post(`/session/${sessionId}/end`);
    return response.data;
  },
  changePhase: async (sessionId: string, phase: string) => {
    const response = await bridgeApi.post(`/session/${sessionId}/phase`, { phase });
    return response.data;
  },

  // Participants
  addParticipant: async (sessionId: string, data: {
    name: string;
    role: string;
    learner_id?: string;
    role_title?: string;
    role_description?: string;
    role_hidden_info?: string;
  }) => {
    const response = await bridgeApi.post(`/session/${sessionId}/participants`, data);
    return response.data;
  },
  removeParticipant: async (sessionId: string, participantId: string) => {
    const response = await bridgeApi.delete(`/session/${sessionId}/participants/${participantId}`);
    return response.data;
  },

  // Role Cards
  releaseRoles: async (sessionId: string) => {
    const response = await bridgeApi.post(`/session/${sessionId}/release-roles`);
    return response.data;
  },
  getRoleStatus: async (sessionId: string) => {
    const response = await bridgeApi.get(`/session/${sessionId}/role-status`);
    return response.data;
  },

  // Twists
  fireTwist: async (sessionId: string, data: {
    twist_id: string;
    title: string;
    intensity: string;
    instructions?: string;
  }) => {
    const response = await bridgeApi.post(`/session/${sessionId}/fire-twist`, data);
    return response.data;
  },
  getTwistStatus: async (sessionId: string) => {
    const response = await bridgeApi.get(`/session/${sessionId}/twist-status`);
    return response.data;
  },

  // Scoring Metadata
  getScoringMetadata: async (sessionId: string) => {
    const response = await bridgeApi.get(`/session/${sessionId}/scoring-metadata`);
    return response.data;
  },
};

// ── Micro-Class API ──────────────────────────────────────────────────
// Uses /api/micro-class prefix
// Consolidation A (2026-06-13) — shares the standard interceptors.
const microClassAxios: AxiosInstance = createScopedAxios('/api/micro-class');

export const microClassApi = {
  // Classes — uses /catalogue endpoint (backend's actual route)
  list: async (params?: { status?: string; skill_id?: string; limit?: number; offset?: number }) => {
    // #117 (Scoring, 30 Sep): /catalogue is the learner list and, without
    // `content_language`, lists English classes only. Admin screens must
    // see every class, so always ask for both languages.
    const response = await microClassAxios.get('/catalogue', { params: { content_language: 'both', ...params } });
    // Backend returns { items: [...] } — unwrap and normalize IDs
    const data = response.data;
    const items = data.microClasses || data.items || data;
    // Catalogue items use micro_class_id; normalize to 'id' + mark as published
    return (Array.isArray(items) ? items : []).map((item: Record<string, unknown>) => ({
      ...item,
      id: item.id || item.micro_class_id || item.microClassId,
      status: 'published',
      skills_assessed: item.skills_assessed || item.skills,
    })) as MicroClassListItem[];
  },
  listDrafts: async (params?: { status?: string }) => {
    const response = await microClassAxios.get('/classes', { params });
    const data = response.data;
    return data.drafts || data.items || data;
  },
  get: async (classId: string) => {
    // Try draft endpoint first (has full form data), fall back to catalogue
    try {
      const response = await microClassAxios.get(`/classes/${classId}`);
      return response.data;
    } catch {
      const response = await microClassAxios.get(`/catalogue/${classId}`);
      return response.data;
    }
  },
  getPublished: async (classId: string) => {
    const response = await microClassAxios.get(`/catalogue/${classId}`);
    return response.data;
  },
  create: async (data: Record<string, unknown>) => {
    const response = await microClassAxios.post('/classes', data);
    return response.data;
  },
  update: async (classId: string, data: Record<string, unknown>) => {
    const response = await microClassAxios.patch(`/classes/${classId}`, data);
    return response.data;
  },
  delete: async (classId: string) => {
    const response = await microClassAxios.delete(`/classes/${classId}`);
    return response.data;
  },
  publish: async (classId: string) => {
    const response = await microClassAxios.post(`/classes/${classId}/publish`);
    return response.data;
  },
  // Off the shelf (Scoring, 7 Oct; replaces the retire route, which never
  // existed). Hidden classes leave every learner door and come back as
  // they were. Put back is refused (422 class_cannot_go_back, with plain
  // `issues`) when the class fails the publish gate.
  takeOffShelf: async (classId: string) => {
    const response = await microClassAxios.post(`/classes/${classId}/take-off-shelf`);
    return response.data as { id: string; title: string; hidden: boolean };
  },
  putBack: async (classId: string) => {
    const response = await microClassAxios.post(`/classes/${classId}/put-back`);
    return response.data as { id: string; title: string; hidden: boolean };
  },
  /** The hidden classes; /catalogue never carries them (it is the learners' list). */
  listOffShelf: async () => {
    const response = await microClassAxios.get('/off-the-shelf');
    return (response.data?.classes ?? []) as Array<{ id: string; title: string; engine_type?: number; content_language?: string; hidden: true }>;
  },

  // Rubrics
  getRubric: async (skillId: string) => {
    const response = await microClassAxios.get(`/rubrics/${skillId}`);
    return response.data;
  },

  // Generation pipeline
  generateScript: async (classId: string, model?: string) => {
    const response = await microClassAxios.post(`/classes/${classId}/generate-script`, model ? { model } : {});
    return response.data;
  },

  // 2026-06-14 (Nino "Ugly list" wave): async variant. CC now prefers
  // this pair so the wizard doesn't hang the HTTP request for the full
  // 30–120 s LLM round-trip.
  //   1. startGenerateScriptAsync()  → returns { job_id }
  //   2. pollGenerateScriptJob()     → resolves when the job hits a
  //      terminal state (success / error), rejects on error.
  startGenerateScriptAsync: async (classId: string, model?: string) => {
    const response = await microClassAxios.post(
      `/classes/${classId}/generate-script/async`,
      model ? { model } : {},
    );
    return response.data as { class_id: string; job_id: string; status: 'queued' };
  },
  getGenerateScriptJob: async (classId: string, jobId: string) => {
    const response = await microClassAxios.get(
      `/classes/${classId}/generate-script/jobs/${jobId}`,
    );
    return response.data as {
      class_id: string;
      job_id: string;
      state: string;
      status: 'queued' | 'running' | 'success' | 'error';
      phase?: string | null;
      pct?: number | null;
      result?: Record<string, unknown> | null;
      error?: string | null;
    };
  },
  /**
   * Convenience wrapper — starts an async generate-script job and
   * polls until terminal. Calls `onProgress` with the latest phase + pct
   * snapshot every poll (CC's wizard wires this to a step indicator).
   *
   * Defaults: 2s poll interval, 5 min ceiling (matches the Celery task's
   * soft_time_limit). Aborts and rejects on AbortSignal cancel.
   */
  generateScriptAsync: async (
    classId: string,
    opts: {
      model?: string;
      pollMs?: number;
      timeoutMs?: number;
      signal?: AbortSignal;
      onProgress?: (snap: { phase: string | null; pct: number | null; status: string }) => void;
    } = {},
  ) => {
    const {
      model,
      pollMs = 2000,
      timeoutMs = 5 * 60_000,
      signal,
      onProgress,
    } = opts;

    const start = await microClassApi.startGenerateScriptAsync(classId, model);
    const jobId = start.job_id;
    const deadline = Date.now() + timeoutMs;

    while (Date.now() < deadline) {
      if (signal?.aborted) {
        throw new DOMException('generate-script polling aborted', 'AbortError');
      }
      const snap = await microClassApi.getGenerateScriptJob(classId, jobId);
      if (onProgress) {
        onProgress({
          phase: snap.phase ?? null,
          pct: snap.pct ?? null,
          status: snap.status,
        });
      }
      if (snap.status === 'success') {
        return snap.result ?? {};
      }
      if (snap.status === 'error') {
        throw new Error(snap.error || 'generate-script job failed');
      }
      // queued / running → wait + repoll
      await new Promise((r) => setTimeout(r, pollMs));
    }
    throw new Error('generate-script job did not complete in time');
  },
  auditSkills: async (
    classId: string,
    opts?: { model?: string; force?: boolean },
  ) => {
    // 2026-06-15 (Nino — "Re-run Validation" returned stale cache):
    // backend caches by content_hash. When the user clicks the
    // explicit Re-run, we want a fresh audit (e.g. after a prompt
    // change). Default false preserves the cache on the first
    // auto-run when entering Step 4.
    const body: Record<string, unknown> = {};
    if (opts?.model) body.model = opts.model;
    if (opts?.force) body.force = true;
    const response = await microClassAxios.post(`/classes/${classId}/audit-skills`, body);
    return response.data;
  },
  // 2026-06-14 (Nino UI wave #179 follow-on): the voice argument used
  // to be silently dropped — the frontend never sent it, so every
  // regeneration used the backend default. Selecting a different
  // voice in Step 3 had no effect on the audio that actually got
  // produced. Now the chosen voice flows all the way through.
  generateAudio: async (classId: string, voice?: string) => {
    const response = await microClassAxios.post(
      `/classes/${classId}/generate-audio`,
      voice ? { voice } : {},
    );
    return response.data;
  },
  getPipelineStatus: async (classId: string) => {
    const response = await microClassAxios.get(`/classes/${classId}/pipeline-status`);
    return response.data;
  },
};



// ── TTS API ──────────────────────────────────────────────────────────
// Uses /api/tts prefix
// Consolidation A (2026-06-13) — shares the standard interceptors.
const ttsAxios: AxiosInstance = createScopedAxios('/api/tts');

export const ttsApi = {
  getVoices: async () => {
    const response = await ttsAxios.get('/voices');
    return response.data;
  },
  getConfig: async () => {
    const response = await ttsAxios.get('/config');
    return response.data;
  },
  updateConfig: async (data: Record<string, unknown>) => {
    const response = await ttsAxios.patch('/config', data);
    return response.data;
  },
  preview: async (data: { text: string; voice_id?: string }) => {
    const response = await ttsAxios.post('/preview', data, { responseType: 'blob' });
    return response.data;
  },
};

// ── Facilitator Analytics API ────────────────────────────────────────
export const facilitatorAnalyticsApi = {
  getFeedback: async (facilitatorId: string) => {
    const response = await api.get(`/analytics/facilitators/${facilitatorId}/feedback`);
    return response.data;
  },
  getScore: async (facilitatorId: string) => {
    const response = await api.get(`/analytics/facilitators/${facilitatorId}/score`);
    return response.data;
  },
  recomputeScore: async (facilitatorId: string) => {
    const response = await api.post(`/analytics/facilitators/${facilitatorId}/score/recompute`);
    return response.data;
  },
  recomputeAll: async () => {
    const response = await api.post('/analytics/scores/recompute-all');
    return response.data;
  },
};

// ── Content Pipeline API ─────────────────────────────────────────────
export const contentPipelineApi = {
  list: async (params?: { status?: string; limit?: number; offset?: number }) => {
    const response = await api.get('/content-pipeline/jobs', { params });
    return response.data;
  },
  get: async (jobId: string) => {
    const response = await api.get(`/content-pipeline/jobs/${jobId}`);
    return response.data;
  },
  retry: async (jobId: string) => {
    const response = await api.post(`/content-pipeline/jobs/${jobId}/retry`);
    return response.data;
  },
  cancel: async (jobId: string) => {
    const response = await api.post(`/content-pipeline/jobs/${jobId}/cancel`);
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/content-pipeline/stats');
    return response.data;
  },
  trigger: async (data: {
    reason?: string;
    target_class_count?: number;
    dry_run?: boolean;
  }) => {
    const response = await api.post('/content-pipeline/trigger', data);
    return response.data;
  },
  triggerBatch: async (data: {
    count?: number;
    reason?: string;
    dry_run?: boolean;
  }) => {
    const response = await api.post('/content-pipeline/trigger/batch', data);
    return response.data;
  },
  getHistory: async (limit?: number) => {
    const response = await api.get('/content-pipeline/history', { params: { limit } });
    return response.data;
  },
  getHealth: async () => {
    const response = await api.get('/content-pipeline/health');
    return response.data;
  },
};

// ── Content Store API ────────────────────────────────────────────────
// Backend routes: /api/v1/content-store/{catalogue|pending|stats|ingest|{id}}
// Note: the shared `api` axios instance already has baseURL '/api/v1'.
// Paths below MUST start with '/content-store/...' — adding a leading
// '/v1' produced '/api/v1/v1/content-store/...' (404) which was the
// long-standing cause of the empty Content Catalogue tab (fixed
// 2026-06-07 from Nino's User Stories doc audit).
export const contentStoreApi = {
  // 2026-06-13 (Nino #4): server-side filters for audience / cultural_context
  // / tone_register / audit_gate now wired through. Client passes all
  // params straight to the backend; no more client-only filter dropping.
  catalogue: async (params?: {
    engine_type?: number;
    limit?: number;
    // 2026-06-14 (Nino unified-catalogue wave): filter chip for the
    // unified MC + Skill Drill view. 'all' (or omit) → union.
    content_type?: 'all' | 'mc' | 'skill_drill';
    audience?: 'university_students' | 'professionals' | 'both';
    cultural_context?: 'general' | 'sea' | 'indonesia' | 'singapore' | 'eu' | 'us';
    tone_register?: 'default' | 'formal' | 'direct' | 'warm';
    audit_gate?: 'green' | 'amber' | 'red';
  }) => {
    const response = await api.get('/content-store/catalogue', { params });
    return response.data; // { total, items }
  },
  get: async (microClassId: string) => {
    const response = await api.get(`/content-store/${microClassId}`);
    return response.data;
  },
  ingest: async (data: { content_json: Record<string, unknown>; source?: string; auto_approve?: boolean }) => {
    const response = await api.post('/content-store/ingest', data);
    return response.data;
  },
  pending: async (params?: { limit?: number }) => {
    const response = await api.get('/content-store/pending', { params });
    return response.data; // { pending_count, items }
  },
  /** One waiting item in full, for "Read it" (asked of Scoring, 7 Oct; 404 until then). */
  pendingItem: async (contentId: string): Promise<{ content_json?: { sections?: unknown[] }; sections?: unknown[] } & Record<string, unknown>> => {
    const response = await api.get(`/content-store/pending/${encodeURIComponent(contentId)}`);
    return response.data;
  },
  review: async (contentId: string, data: { action: 'approve' | 'reject' | 'edit'; edit_json?: Record<string, unknown>; reviewer_notes?: string }) => {
    const response = await api.post(`/content-store/pending/${contentId}/review`, data);
    return response.data;
  },
  stats: async () => {
    const response = await api.get('/content-store/stats');
    return response.data;
  },
  delete: async (microClassId: string) => {
    const response = await api.delete(`/content-store/${microClassId}`);
    return response.data;
  },
  // PB-102 — edit a class's daily_hook from CC's ContentCatalogue tab.
  // Pass null (or empty string) to clear the field.
  //
  // 2026-08-06 (DIST 15 Tools #1): kept as a thin wrapper on top of
  // updatePublishedFields for existing call-sites; new code should use
  // updatePublishedFields directly so multi-field edits go in one PATCH.
  updateDailyHook: async (
    microClassId: string,
    dailyHook: string | null,
  ) => {
    return contentStoreApi.updatePublishedFields(microClassId, { daily_hook: dailyHook });
  },

  // 2026-08-06 (DIST 15 Tools #1): CC edit path for published classes.
  // Extends the existing PATCH /content-store/{id}/daily-hook route so
  // the same door handles the whole presentation-edit family
  // (title / description / daily_hook, and later anchors when Scoring
  // and CA agree the shape).
  //
  // All fields are optional; only the ones the author actually changed
  // are sent so the backend doesn't need to distinguish "not touched"
  // from "cleared". Pass `null` (or empty string for daily_hook) to
  // explicitly clear a field.
  //
  // Substance edits (scenario, sections, participant actions) are OUT
  // of scope for this route — those go through Scoring's re-publish
  // path and trigger past-completer exclusion (DIST 15 Scoring section).
  updatePublishedFields: async (
    microClassId: string,
    fields: {
      title?: string;
      description?: string | null;
      daily_hook?: string | null;
    },
  ) => {
    const response = await api.patch(
      `/content-store/${microClassId}/daily-hook`,
      fields,
    );
    return response.data as {
      micro_class_id: string;
      title?: string;
      description?: string | null;
      daily_hook?: string | null;
      edited_at: string;
    };
  },
};

// ── Notification API ─────────────────────────────────────────────────
export const notificationApi = {
  list: async (params?: { type?: string; read?: boolean; limit?: number }) => {
    const response = await api.get('/notifications', { params });
    return response.data;
  },
  markRead: async (notificationId: string) => {
    const response = await api.post(`/notifications/${notificationId}/read`);
    return response.data;
  },
  markAllRead: async () => {
    const response = await api.post('/notifications/read-all');
    return response.data;
  },
};





// ── Archive API ─────────────────────────────────────────────────────
export const archiveApi = {
  list: async (params?: {
    page?: number;
    page_size?: number;
    content_type?: string;
    status?: string;
    search?: string;
  }) => {
    const response = await api.get('/archive/generations', { params });
    return response.data;
  },

  get: async (generationId: number) => {
    const response = await api.get(`/archive/generations/${generationId}`);
    return response.data;
  },

  create: async (data: {
    content_type: string;
    content_name: string;
    engine_type?: number;
    engine_name?: string;
    skills_assessed?: string[];
    difficulty?: string;
    model: string;
    prompt: string;
    output?: unknown;
    status: string;
    error_message?: string;
    tokens_in?: number;
    tokens_out?: number;
    latency_ms?: number;
    created_by?: string;
  }) => {
    const response = await api.post('/archive/generations', data);
    return response.data;
  },
};

// ── Programme API ────────────────────────────────────────────────────
// CC Overview tab — consolidated snapshot endpoint (2026-06-07, slice 4a).
// Each top-level block can be null if the backend's per-block query
// failed; the UI renders "—" for any block that comes back null.
export interface AdminOverview {
  as_of: string;
  learners: {
    total: number;
    active_last_30d: number;
    by_audience: Record<string, number>;
    onboarding_completion_pct: number;
  } | null;
  facilitators: {
    total: number;
    active: number;
    average_rating: number | null;
  } | null;
  catalogue: {
    micro_classes_count: number;
    skill_drills_count: number | null;
  } | null;
  activity: {
    micro_class_attempts_total: number;
    micro_class_attempts_by_month: { month: string; count: number }[];
    skill_drill_attempts_total: number | null;
    skill_drill_attempts_by_month: { month: string; count: number }[] | null;
  } | null;
  popularity: {
    top_micro_class: { id: string; count: number } | null;
    top_skill_drill: { id: string; count: number } | null;
  } | null;
  heatmap: {
    cells: {
      pillar: string;
      distribution: [number, number, number, number, number];
      avg_score: number;
      total_learners: number;
    }[];
  } | null;
}

// The menu's "needs work" counts (Scoring, 7 Oct): one light read instead
// of downloading every draft. Polling, so an expired sign-in never logs out.
const adminCountsAxios: AxiosInstance = createScopedAxios('/api/admin', { skip401Redirect: true });

/**
 * WhatsApp a facilitator or a learner from Operations (Live Drills 4aeb0783): staff only, the server
 * answers {url: wa.me/...} or 404 no_whatsapp. The page opens the link and never shows the number.
 */
export const whatsappApi = {
  facilitator: async (id: string): Promise<string | null> => waUrl(`/facilitators/${encodeURIComponent(id)}/whatsapp`),
  learner: async (id: string): Promise<string | null> => waUrl(`/learners/${encodeURIComponent(id)}/whatsapp`),
};

async function waUrl(path: string): Promise<string | null> {
  try {
    const response = await adminCountsAxios.get<{ url?: string }>(path);
    return typeof response.data?.url === 'string' && response.data.url.startsWith('https://wa.me/') ? response.data.url : null;
  } catch (err) {
    if ((err as { response?: { status?: number } }).response?.status === 404) return null;
    throw err;
  }
}

export const adminOverviewApi = {
  needsWorkCounts: async (): Promise<{ skill_drills: { draft: number; ready: number }; classes: { draft: number; ready: number } }> => {
    const response = await adminCountsAxios.get('/needs-work-counts');
    return response.data;
  },
  get: async (): Promise<AdminOverview> => {
    const response = await api.get('/admin/overview');
    return response.data;
  },
  // Sidebar Phase 2 (2026-06-13): live counts for the 3 badge slots.
  // Tolerant endpoint — returns 0 when underlying tables are missing,
  // never errors. Uses apiPolling so a 401 (expired token mid-session)
  // does NOT trigger the global force-logout interceptor.
  sidebarCounters: async (): Promise<{
    ops_incidents: number;
    workshop_reviews_new: number;
    facilitators_promotion_ready: number;
    as_of: string;
  }> => {
    const response = await apiPolling.get('/admin/sidebar-counters');
    return response.data;
  },
};

// The coaching calendar (one day or a range), for the Operations page's timeline and tomorrow's check.
export interface CalendarSession {
  event_id: string;
  event_type: 'workshop' | 'ai_coach' | 'live_coach' | 'ai_coach_slot' | 'live_coach_slot' | string;
  starts_at: string | null;
  ends_at: string | null;
  status: string;
  title: string;
  facilitator_name: string | null;
  learner_count: number;
  max_participants: number;
  link: string | null;
  // Live Drills' tomorrow's check (asked 18 Oct); absent until they ship.
  min_participants?: number | null;
  venue?: { name: string; confirmed?: boolean | null } | null;
}
/** One slot from the scheduler's calendar (the learner site's list): real sessions and possible ones. */
export interface SchedulerSlot {
  session_id: string | null;
  is_materialised: boolean;
  slot_signature?: string | null;
  facilitator_name: string | null;
  workshop_title: string;
  workshop_short_title?: string | null;
  scheduled_start: string;
  scheduled_end?: string | null;
  duration_minutes?: number | null;
  min_participants?: number | null;
  max_participants?: number | null;
  current_bookings?: number | null;
  status: string;
}
export const calendarApi = {
  range: async (dateFrom: string, dateTo: string): Promise<{ events: CalendarSession[] }> => {
    const response = await adminCountsAxios.get('/coaching-calendar', { params: { date_from: dateFrom, date_to: dateTo } });
    return response.data;
  },
  /** Facilitated sessions (Operations): the scheduler's calendar, the same list the learner site books from. */
  sessions: async (dateFrom: string, dateTo: string): Promise<{ slots: SchedulerSlot[] }> => {
    const response = await schedulerApi.get('/sessions/calendar', { params: { date_from: dateFrom, date_to: dateTo } });
    return response.data;
  },
};

// Health › Systems (Platform's summary, 17 Oct): figures only, never keys.
export const systemsApi = {
  get: async (): Promise<import('@/lib/systems').SystemsSummary> => {
    const response = await apiPolling.get('/admin/systems');
    return response.data;
  },
};

export const learnersApi = {
  // 2026-06-07 — audience_type added for CC LearnerManagement sub-tabs
  // (B2C / B2U / B2B). Backend enum: on_my_own | university | employer.
  list: async (params?: {
    search?: string;
    status?: string;
    onboarding?: string;
    audience_type?: 'on_my_own' | 'university' | 'employer';
    // Learners v1 (Scoring, 12 Oct): server filters.
    organisation_id?: string;
    city?: string;
    career_stage?: string;
    language?: string;
    state?: string;
    limit?: number;
    offset?: number;
  }) => {
    const response = await api.get('/learners', { params });
    return response.data;
  },
  // 2026-10-12 — the same filters as the list (Scoring's version 1), so the
  // headline numbers follow them; older servers ignore them.
  getStats: async (params?: Record<string, string | undefined>) => {
    const response = await api.get('/learners/stats', { params });
    return response.data;
  },
  get: async (learnerId: string) => {
    const response = await api.get(`/learners/${learnerId}`);
    return response.data;
  },
  getProgression: async (learnerId: string) => {
    const response = await api.get(`/learners/${learnerId}/progression`);
    return response.data;
  },
  getSkillHistory: async (learnerId: string, skillId: string) => {
    const response = await api.get(`/learners/${learnerId}/progression/${skillId}/history`);
    return response.data;
  },
  delete: async (learnerId: string) => {
    const response = await api.delete(`/learners/${learnerId}`);
    return response.data;
  },
};

export const programmeApi = {
  list: async (params?: { status?: string; mode?: string; learner_id?: string; limit?: number; offset?: number }) => {
    const response = await api.get('/programmes', { params });
    return response.data;
  },
  get: async (programmeId: string) => {
    const response = await api.get(`/programmes/${programmeId}`);
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/programmes/stats');
    return response.data;
  },
  create: async (data: Record<string, unknown>) => {
    const response = await api.post('/programmes', data);
    return response.data;
  },
  updateStatus: async (programmeId: string, data: { status: string; reason?: string }) => {
    const response = await api.put(`/programmes/${programmeId}/status`, data);
    return response.data;
  },
  update: async (programmeId: string, data: Record<string, unknown>) => {
    const response = await api.patch(`/programmes/${programmeId}`, data);
    return response.data;
  },
};

// ── Workshop Scoring API ──────────────────────────────────────────────
// W6.6 (2026-04-28): facilitator-driven recording upload + scoring
// trigger. The facilitator picks a ScheduledSession in the FP, drags
// in a Zoom recording / onsite audio file, the file goes to S3 via a
// pre-signed URL, then submit() enqueues the W6.1 chord. Status polling
// drives the "Queued for scoring" panel until the session's debrief
// becomes available.

// Consolidation A (2026-06-13) — shares the standard interceptors.
// The old FP-path-check redirect skip is dead now that FP is a
// separate SPA at /facilitator/ (different window).
const workshopScoringAxios: AxiosInstance = createScopedAxios('/api/workshop');

export interface WorkshopUploadUrlResponse {
  upload_url: string;
  s3_key: string;
  session_id: string;
  expires_in: number;
  max_size_bytes: number;
}

export interface WorkshopSubmitResponse {
  job_id: string;
  status: string;
  estimated_processing_minutes: number;
  poll_url: string;
}

export interface WorkshopScoringStatusResponse {
  status: 'queued' | 'processing' | 'awaiting_mapping' | 'complete' | 'failed';
  progress: number;
  scores_available: boolean;
  status_message?: string;
  stage?: string;
  started_at?: string;
  completed_at?: string;
  speakers_detected?: number;
}

export const workshopScoringApi = {
  /**
   * Step 1: ask for a pre-signed S3 PUT URL. Server validates file
   * size + content type, returns the URL plus the s3_key we'll pass
   * back on submit.
   */
  getUploadUrl: async (
    workshopId: string,
    body: {
      session_id?: string;
      file_name: string;
      file_size_bytes: number;
      content_type: string;
    },
  ): Promise<WorkshopUploadUrlResponse> => {
    const response = await workshopScoringAxios.post(
      `/${encodeURIComponent(workshopId)}/upload-url`,
      body,
    );
    return response.data;
  },

  /**
   * Step 2: PUT the file directly to S3 via the pre-signed URL. We
   * use a fresh axios instance (no auth interceptor — S3 will reject
   * a Bearer header). onUploadProgress drives the progress bar.
   */
  uploadToS3: async (
    uploadUrl: string,
    file: File,
    contentType: string,
    onProgress: (pct: number) => void,
  ): Promise<void> => {
    await axios.put(uploadUrl, file, {
      headers: { 'Content-Type': contentType },
      onUploadProgress: (event) => {
        if (event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
      // Don't transform — S3 wants the file body as-is.
      transformRequest: [(data) => data],
    });
  },

  /**
   * Step 3: enqueue the scoring job. Server returns a job_id and a
   * poll URL for status. Triggers the W6.1 chord under the hood.
   */
  submit: async (
    workshopId: string,
    body: {
      session_id: string;
      s3_key: string;
      session_mode: 'remote' | 'onsite' | 'hybrid';
      recording_duration_sec?: number;
      bridge_session_id?: string;
      // PB-184 Flavour A (2026-05-09): pre-diarization declaration
      // of who was in the session. `speaker_label` is left null
      // because the FP facilitator submits this at upload time —
      // before diarization runs. Resolved post-diarization on the
      // backend via heuristic pairing.
      participant_mapping?: Array<{
        speaker_label?: string | null;
        learner_id?: string | null;
        display_name?: string | null;
        role?: 'participant' | 'facilitator';
      }>;
    },
  ): Promise<WorkshopSubmitResponse> => {
    const response = await workshopScoringAxios.post(
      `/${encodeURIComponent(workshopId)}/submit`,
      body,
    );
    return response.data;
  },

  /**
   * Status polling for the post-submit confirmation panel. Used by
   * the upload tab to flip from "Uploading…" → "Queued for scoring"
   * → "Scoring in progress" → "Complete".
   */
  getStatus: async (
    workshopId: string,
    sessionId: string,
  ): Promise<WorkshopScoringStatusResponse> => {
    const response = await workshopScoringAxios.get(
      `/${encodeURIComponent(workshopId)}/sessions/${encodeURIComponent(sessionId)}/status`,
    );
    return response.data;
  },
};

// ── Themes CRUD API (PB-193, 2026-05-09) ──────────────────────────
// CC theme management surface. CRUD-on-flat-file (data/focus_themes.json)
// preserves the "edit-then-commit" living-list pattern while giving CC
// a live edit affordance. Mutations invalidate the backend's lru_cache.

export interface ThemeSkillBody {
  skill_id: string;
  weight: number;
  is_core: boolean;
}

export interface ThemeBody {
  id: string;
  name: string;
  description: string;
  skills: ThemeSkillBody[];
}

// Consolidation A (2026-06-13) — previously had NO interceptors:
// requests went out without the Authorization header, relying on
// dev-mode auth bypass. Standard interceptors fix this silently.
const themesAxios: AxiosInstance = createScopedAxios('/api/themes');

export const themesApi = {
  list: async (): Promise<{ themes: ThemeBody[]; total_themes?: number }> => {
    const r = await themesAxios.get('');
    return r.data;
  },
  create: async (body: ThemeBody): Promise<ThemeBody> => {
    const r = await themesAxios.post('', body);
    return r.data;
  },
  update: async (themeId: string, body: ThemeBody): Promise<ThemeBody> => {
    const r = await themesAxios.put(`/${encodeURIComponent(themeId)}`, body);
    return r.data;
  },
  remove: async (themeId: string): Promise<void> => {
    await themesAxios.delete(`/${encodeURIComponent(themeId)}`);
  },
};

// ── Career role display-name conversion matrix (Nino 2026-06-20) ────
// CC editor for the INDOTASK → Playbook short-display-name table.
// Backend mounts at /api/admin/career/role-display-names.

export interface RoleDisplayNameRow {
  kbji_code: string;
  official_name: string;
  display_name: string;
  is_overridden: boolean;
  category: string;
  skill_level: string;
}

export interface RoleDisplayNamesResponse {
  rows: RoleDisplayNameRow[];
  total: number;
  override_count: number;
}

const careerRolesAxios: AxiosInstance = createScopedAxios(
  '/api/admin/career/role-display-names',
);

// ── Admin Venues (CC view, 2026-06-21 task #231) ─────────────────
// Cross-org venue CRUD for Playbook staff. Endpoints live at
// /api/admin/venues and require role="admin" — same JWT CC already
// uses. `organization_id` filters reads to a single org;
// create requires it in the body. Same backing table as /api/org/venues
// so OP and CC see the same rows.

export interface Venue {
  id: string;
  organization_id: string;
  name: string;
  address: string | null;
  region: string | null;
  capacity: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  is_archived?: boolean;
}

export interface VenueWriteBody {
  name: string;
  organization_id: string;
  address?: string | null;
  region?: string | null;
  capacity?: number | null;
  notes?: string | null;
}

const adminVenuesAxios: AxiosInstance = createScopedAxios('/api/admin/venues');

export const adminVenuesApi = {
  list: async (params?: {
    organization_id?: string;
    include_archived?: boolean;
  }): Promise<Venue[]> => {
    const r = await adminVenuesAxios.get('', {
      params: {
        organization_id: params?.organization_id,
        include_archived: params?.include_archived ? 'true' : 'false',
      },
    });
    return Array.isArray(r.data) ? r.data : r.data?.venues ?? [];
  },
  create: async (body: VenueWriteBody): Promise<Venue> => {
    const r = await adminVenuesAxios.post('', body);
    return r.data;
  },
  update: async (
    venueId: string,
    body: Partial<Omit<VenueWriteBody, 'organization_id'>>,
  ): Promise<Venue> => {
    const r = await adminVenuesAxios.patch(`/${encodeURIComponent(venueId)}`, body);
    return r.data;
  },
  archive: async (venueId: string): Promise<void> => {
    await adminVenuesAxios.delete(`/${encodeURIComponent(venueId)}`);
  },
};

// Back-compat export — old name still imported by Venues.tsx. Will
// rename in a follow-up commit if anyone cares.
export const orgVenuesApi = adminVenuesApi;


export const careerRoleDisplayNamesApi = {
  list: async (): Promise<RoleDisplayNamesResponse> => {
    const r = await careerRolesAxios.get('');
    return r.data;
  },
  update: async (
    kbjiCode: string,
    displayName: string,
  ): Promise<RoleDisplayNameRow> => {
    const r = await careerRolesAxios.patch(
      `/${encodeURIComponent(kbjiCode)}`,
      { display_name: displayName },
    );
    return r.data;
  },
  reset: async (kbjiCode: string): Promise<RoleDisplayNameRow> => {
    const r = await careerRolesAxios.delete(
      `/${encodeURIComponent(kbjiCode)}`,
    );
    return r.data;
  },
};

// ── Career Library — full-corpus display-name editor (Scoring 2026-07-16) ──
// Superset of careerRoleDisplayNamesApi. Reads all ~2,250 router
// occupations across O*NET / ESCO / WEF / INDOTASK, with Future Fit
// Score + pillar breakdown surfaced per row. Path uses FastAPI `:path`
// converter so ESCO UUIDs + O*NET codes pass through without URL
// encoding (per Scoring memo — do not `encodeURIComponent`).

export type CorpusSource = 'onet' | 'esco' | 'wef' | 'indotask';

export interface CorpusFutureFitPillars {
  defensibility: number;
  financial: number;
  quality_of_life: number;
  societal_value: number;
}

export interface CorpusRow {
  occupation_id: string;
  source: CorpusSource;
  source_native_id: string;
  official_name: string;
  display_name: string;
  is_overridden: boolean;
  industry: string;
  industry_label: string;
  skill_level: string;
  /** 0-100. Null when the model has no signal for the occupation. */
  future_fit_score: number | null;
  future_fit_pillars: CorpusFutureFitPillars | null;
}

export interface CorpusResponse {
  rows: CorpusRow[];
  total: number;
  override_count: number;
  high_future_fit_count: number;
}

const careerCorpusAxios: AxiosInstance = createScopedAxios(
  '/api/admin/career/corpus-display-names',
);

export const careerCorpusApi = {
  list: async (): Promise<CorpusResponse> => {
    const r = await careerCorpusAxios.get('');
    return r.data;
  },
  update: async (
    occupationId: string,
    displayName: string,
  ): Promise<CorpusRow> => {
    // No encodeURIComponent — Scoring's `:path` converter handles
    // dots + hyphens + UUIDs raw. Encoding would double-encode `%`.
    const r = await careerCorpusAxios.patch(`/${occupationId}`, {
      display_name: displayName,
    });
    return r.data;
  },
  reset: async (occupationId: string): Promise<CorpusRow> => {
    const r = await careerCorpusAxios.delete(`/${occupationId}`);
    return r.data;
  },
};

// ── Scenario Programmes API (CC Scenario Builder, 2026-05-16) ────────────
// CC tool that generates multi-class programmes anchored to a Scenario.
// 9 endpoints under `/api/cc/scenario-programmes`. See spec at
// `Documentation/specs/scenario-builder-v1.md`.

export type ScenarioProgrammeStatus =
  | 'draft'
  | 'outline_ready'
  | 'generating'
  | 'ready'
  | 'published'
  | 'archived';

export type ScenarioClassGenerationStatus =
  | 'pending'
  | 'generating'
  | 'ready'
  | 'failed';

export type ScenarioClassBreakdown = 'mc_only' | 'sd_only' | 'mix';
export type ScenarioDifficulty = 'easy' | 'medium' | 'hard' | 'mixed';
// Split per Nino 2026-05-17: MC vs SD have different natural ranges.
export type ScenarioMCDurationTarget = '5' | '10' | '15' | 'mixed';
export type ScenarioSDDurationTarget = '30' | '45' | '60' | '90' | 'mixed';
// Coach type for skill drills — locked 2026-05-27 via the drill catalogue
// v3.20 (2 tiers × 4 durations = 8 SKUs). 'ai' routes scoring through the
// async Sonnet-only path; 'live' routes through the facilitator marketplace
// + workshop scoring chord. Credit costs per tier are config values
// supplied by Business Model — not baked into the schema.
export type ScenarioSDCoachType = 'ai' | 'live';
export const SCENARIO_SD_COACH_TYPE_LABELS: Record<ScenarioSDCoachType, string> = {
  ai: 'AI Coach',
  live: 'Live Coach',
};
export type ScenarioTone = 'friendly' | 'direct' | 'formal';
// Planning-pass LLM (CC user selects on create). Kept in sync with
// backend `VALID_PLANNING_MODEL` + migration 035 CHECK constraint.
export type ScenarioPlanningModel =
  | 'claude-opus-4-6'
  | 'claude-sonnet-4-6'
  | 'claude-haiku-4-5-20251001'
  | 'gemini-2.5-pro';

export const SCENARIO_PLANNING_MODEL_LABELS: Record<ScenarioPlanningModel, string> = {
  'claude-opus-4-6': 'Claude Opus 4.6 (strongest reasoning)',
  'claude-sonnet-4-6': 'Claude Sonnet 4.6 (balanced — default)',
  'claude-haiku-4-5-20251001': 'Claude Haiku 4.5 (fastest)',
  'gemini-2.5-pro': 'Gemini 2.5 Pro',
};

export interface ScenarioOutlineRow {
  position: number;
  title: string;
  one_line: string;
  type: 'mc' | 'sd';
  anchor_skill_id: string;
  difficulty: 'easy' | 'medium' | 'hard';
  duration_target_min: number;
  /** 1..15 for mc rows (LLM-picked by Pass 1 planner); null for sd rows. */
  engine_type?: number | null;
  /** Plot id (e.g. "plot_001") for sd rows; null for mc rows. v1.1+. */
  plot_id?: string | null;
  rationale?: string;
}

export interface ScenarioClassSlot {
  position: number;
  class_id: string | null;
  outline_row: ScenarioOutlineRow | Record<string, unknown>;
  generation_status: ScenarioClassGenerationStatus;
  generation_error: string | null;
}

export interface ScenarioProgramme {
  programme_id: string;
  scenario_slug: string;
  name: string;
  audience_context: string | null;
  class_count: number;
  class_breakdown: ScenarioClassBreakdown;
  difficulty: ScenarioDifficulty;
  mc_duration_target: ScenarioMCDurationTarget;
  sd_duration_target: ScenarioSDDurationTarget;
  /** AI Coach (Sonnet scoring) vs Live Coach (facilitator marketplace).
   *  Locked 2026-05-27 with drill catalogue v3.20. Optional on legacy
   *  programmes — server returns null until backfilled. */
  sd_coach_type: ScenarioSDCoachType | null;
  /** Always an array; empty when no emphasis specified. */
  anchor_skill_emphasis: string[];
  tone: ScenarioTone;
  planning_model: ScenarioPlanningModel;
  outline_json: ScenarioOutlineRow[] | null;
  status: ScenarioProgrammeStatus;
  created_by: string;
  created_at: string | null;
  published_at: string | null;
  classes: ScenarioClassSlot[];
}

export interface CreateScenarioProgrammeBody {
  scenario_slug: string;
  name: string;
  audience_context: string | null;
  class_count: number;
  class_breakdown: ScenarioClassBreakdown;
  difficulty: ScenarioDifficulty;
  mc_duration_target: ScenarioMCDurationTarget;
  sd_duration_target: ScenarioSDDurationTarget;
  /** AI Coach vs Live Coach — defaults to 'ai' on the server when
   *  omitted, so existing call sites keep working. */
  sd_coach_type?: ScenarioSDCoachType;
  anchor_skill_emphasis: string[] | null;
  tone: ScenarioTone;
  planning_model?: ScenarioPlanningModel;
}

// Consolidation A (2026-06-13) — see themesAxios note.
const scenarioProgrammesAxios: AxiosInstance = createScopedAxios('/api/cc/scenario-programmes');

export const scenarioProgrammesApi = {
  list: async (filters: {
    scenario_slug?: string;
    status?: ScenarioProgrammeStatus;
    limit?: number;
  } = {}): Promise<{ programmes: ScenarioProgramme[] }> => {
    const r = await scenarioProgrammesAxios.get('', { params: filters });
    return r.data;
  },
  create: async (body: CreateScenarioProgrammeBody): Promise<ScenarioProgramme> => {
    const r = await scenarioProgrammesAxios.post('', body);
    return r.data;
  },
  get: async (programme_id: string): Promise<ScenarioProgramme> => {
    const r = await scenarioProgrammesAxios.get(`/${encodeURIComponent(programme_id)}`);
    return r.data;
  },
  plan: async (programme_id: string): Promise<ScenarioProgramme> => {
    const r = await scenarioProgrammesAxios.post(`/${encodeURIComponent(programme_id)}/plan`);
    return r.data;
  },
  updateOutline: async (
    programme_id: string,
    outline: ScenarioOutlineRow[],
  ): Promise<ScenarioProgramme> => {
    const r = await scenarioProgrammesAxios.patch(
      `/${encodeURIComponent(programme_id)}/outline`,
      { outline },
    );
    return r.data;
  },
  generate: async (programme_id: string): Promise<ScenarioProgramme> => {
    const r = await scenarioProgrammesAxios.post(`/${encodeURIComponent(programme_id)}/generate`);
    return r.data;
  },
  regenerateClass: async (
    programme_id: string,
    position: number,
  ): Promise<ScenarioClassSlot> => {
    const r = await scenarioProgrammesAxios.post(
      `/${encodeURIComponent(programme_id)}/classes/${position}/regenerate`,
    );
    return r.data;
  },
  publish: async (programme_id: string): Promise<ScenarioProgramme> => {
    const r = await scenarioProgrammesAxios.post(`/${encodeURIComponent(programme_id)}/publish`);
    return r.data;
  },
  archive: async (programme_id: string): Promise<ScenarioProgramme> => {
    const r = await scenarioProgrammesAxios.post(`/${encodeURIComponent(programme_id)}/archive`);
    return r.data;
  },
  reset: async (programme_id: string): Promise<ScenarioProgramme> => {
    const r = await scenarioProgrammesAxios.post(`/${encodeURIComponent(programme_id)}/reset`);
    return r.data;
  },
  remove: async (programme_id: string): Promise<void> => {
    await scenarioProgrammesAxios.delete(`/${encodeURIComponent(programme_id)}`);
  },
};

// Scenarios catalogue (for the dropdown on the create form). Reads from
// the public `/api/scenarios` endpoint — same surface Web uses.
// Consolidation A (2026-06-13) — see themesAxios note.
const scenariosAxios: AxiosInstance = createScopedAxios('/api/scenarios');

export interface ScenarioListItem {
  id: string;
  slug: string;
  name: string;
  pitch: string;
  anchor_skills: string[];
  pillar_mix: string[];
}

export const scenariosApi = {
  list: async (): Promise<{ scenarios: ScenarioListItem[] }> => {
    const r = await scenariosAxios.get('');
    return r.data;
  },
};

// ── Workshop Builder API ──────────────────────────────────────────────
// Uses /api/workshop prefix (scoring-engine backend)
// Consolidation A (2026-06-13) — shares the standard interceptors.
const skillDrillBuilderAxios: AxiosInstance = createScopedAxios('/api/workshop');

/** What a finished generate-blueprint returns (sync route or async job alike). */
export interface GenerateBlueprintResult {
  draft_id: string;
  status?: string;
  generated_content?: unknown;
  content?: unknown;
  prompt_used?: string;
}

export const skillDrillBuilderApi = {
  // Completeness check (Nino 4 Oct; format agreed with Scoring 4 Oct).
  check: async (draftId: string): Promise<CompletenessResult> => {
    const response = await skillDrillBuilderAxios.post(`/drafts/${draftId}/check`);
    return response.data;
  },
  signoff: async (draftId: string, code: string, by: string): Promise<CompletenessResult> => {
    const response = await skillDrillBuilderAxios.post(`/drafts/${draftId}/check/signoff`, { code, by });
    return response.data;
  },
  /** The stored result (draft id = workshop id; Scoring, 4 Oct). */
  storedCheck: async (draftId: string): Promise<CompletenessResult> => {
    const response = await skillDrillBuilderAxios.get(`/drafts/${draftId}/check`);
    return response.data;
  },

  // Draft CRUD
  create: async (data: Record<string, unknown>) => {
    const response = await skillDrillBuilderAxios.post('/drafts', data);
    return response.data;
  },
  get: async (draftId: string) => {
    const response = await skillDrillBuilderAxios.get(`/drafts/${draftId}`);
    return response.data;
  },
  update: async (draftId: string, data: Record<string, unknown>) => {
    const response = await skillDrillBuilderAxios.patch(`/drafts/${draftId}`, data);
    return response.data;
  },
  delete: async (draftId: string) => {
    const response = await skillDrillBuilderAxios.delete(`/drafts/${draftId}`);
    return response.data;
  },
  list: async (params?: { status?: string; limit?: number; offset?: number }) => {
    const response = await skillDrillBuilderAxios.get('/drafts', { params });
    return response.data;
  },

  // Generation pipeline
  generateBlueprint: async (draftId: string) => {
    const response = await skillDrillBuilderAxios.post(`/drafts/${draftId}/generate-blueprint`);
    return response.data;
  },
  /**
   * Generate as a background job, then check on it (Scoring, 6 Oct: the direct
   * route takes about a minute, the front door cuts the browser off before
   * that, and the same paid run went round ~10 times).
   *
   * The start is sent ONCE and never retried: a new run is the author's click.
   * Only the status check (a read, free) is repeated. If a run is already going
   * for this draft (409), we follow that run instead of starting another.
   */
  generateBlueprintAsync: async (
    draftId: string,
    opts: {
      pollMs?: number;
      timeoutMs?: number;
      onProgress?: (snap: { phase: string | null; pct: number | null; status: string }) => void;
    } = {},
  ): Promise<GenerateBlueprintResult> => {
    const { pollMs = 3000, timeoutMs = 10 * 60_000, onProgress } = opts;
    let jobId: string;
    try {
      const start = await skillDrillBuilderAxios.post(`/drafts/${draftId}/generate-blueprint/async`);
      jobId = start.data.job_id;
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { detail?: { existing_job_id?: string } } } };
      const existing = err.response?.status === 409 ? err.response.data?.detail?.existing_job_id : undefined;
      if (!existing) throw e;
      jobId = existing;
    }
    const deadline = Date.now() + timeoutMs;
    let readFailures = 0;
    while (Date.now() < deadline) {
      let snap: { status: string; phase?: string | null; pct?: number | null; result?: Record<string, unknown> | null; error?: string | null };
      try {
        snap = (await skillDrillBuilderAxios.get(`/drafts/${draftId}/generate-blueprint/jobs/${jobId}`)).data;
        readFailures = 0;
      } catch (e) {
        // A failed status read doesn't stop the run on the server; try a few times.
        readFailures += 1;
        if (readFailures >= 5) throw e;
        await new Promise((r) => setTimeout(r, pollMs));
        continue;
      }
      onProgress?.({ phase: snap.phase ?? null, pct: snap.pct ?? null, status: snap.status });
      if (snap.status === 'success') return { draft_id: draftId, ...(snap.result ?? {}) } as GenerateBlueprintResult;
      if (snap.status === 'error') throw new Error(snap.error || 'The generation failed on the server.');
      await new Promise((r) => setTimeout(r, pollMs));
    }
    throw new Error('Still generating after 10 minutes. Open the draft from the Library later: the result lands there when it finishes.');
  },
  publish: async (draftId: string) => {
    const response = await skillDrillBuilderAxios.post(`/drafts/${draftId}/publish`);
    return response.data;
  },
  getPipelineStatus: async (draftId: string) => {
    const response = await skillDrillBuilderAxios.get(`/drafts/${draftId}/pipeline-status`);
    return response.data;
  },

  // Reference data
  getSkillTree: async () => {
    const response = await skillDrillBuilderAxios.get('/reference/skill-tree');
    return response.data;
  },
  getPlots: async () => {
    const response = await skillDrillBuilderAxios.get('/reference/plots');
    return response.data;
  },
  getFrameworks: async () => {
    const response = await skillDrillBuilderAxios.get('/reference/frameworks');
    return response.data;
  },
  getTwists: async (plotId?: string) => {
    const response = await skillDrillBuilderAxios.get('/reference/twists', {
      params: plotId ? { plot_id: plotId } : undefined,
    });
    return response.data;
  },

  // Audit
  audit: async (draftId: string, model?: string, force?: boolean) => {
    const response = await skillDrillBuilderAxios.post(`/drafts/${draftId}/audit`, {
      ...(model ? { model } : {}),
      ...(force ? { force: true } : {}),
    });
    return response.data;
  },

  // PB-45 — pre-generation pillar coverage gate. Run before
  // generate-blueprint so a builder doesn't burn an LLM call on a
  // plot+twist combo whose 5C distribution will produce an
  // unbalanced workshop.
  validatePillarCoverage: async (draftId: string): Promise<{
    draft_id: string;
    gate: 'green' | 'amber' | 'red';
    total_skill_count: number;
    coverage: Array<{
      pillar: string;
      skill_count: number;
      coverage_pct: number;
      status: 'ok' | 'thin' | 'missing';
    }>;
    recommendations: Array<{
      pillar: string;
      reason: string;
      suggested_action: string;
    }>;
  }> => {
    const response = await skillDrillBuilderAxios.post(`/drafts/${draftId}/validate-pillar-coverage`);
    return response.data;
  },

  // PB-46 — pre-publish facilitator walkthrough gate. Validates that
  // generated acts + twists + debrief fit the declared duration with a
  // 10% transition buffer; flags timing overrun + missing
  // facilitator_prep + twist-cue issues.
  facilitatorWalkthrough: async (draftId: string): Promise<{
    draft_id: string;
    gate: 'green' | 'amber' | 'red';
    timeline_valid: boolean;
    runsheet: Array<{
      order: number;
      section_type: string;
      label: string;
      duration_minutes: number;
      cumulative_minutes: number;
      notes: string | null;
    }>;
    timing: {
      declared_duration_minutes: number;
      estimated_duration_minutes: number;
      headroom_minutes: number;
      fits: boolean;
      over_by_minutes: number | null;
    };
    issues: Array<{
      severity: 'warn' | 'error';
      section: string | null;
      message: string;
    }>;
  }> => {
    const response = await skillDrillBuilderAxios.post(`/drafts/${draftId}/facilitator-walkthrough`);
    return response.data;
  },
};

// Card Library API (Nudge and Knowledge Cards)
export const cardApi = {
  // Nudge cards
  listNudge: async (params?: Record<string, unknown>) => {
    const r = await api.get('/cards/nudge', { params });
    return r.data;
  },
  getNudge: async (id: string) => {
    const r = await api.get(`/cards/nudge/${id}`);
    return r.data;
  },
  createNudge: async (data: Record<string, unknown>) => {
    const r = await api.post('/cards/nudge', data);
    return r.data;
  },
  updateNudge: async (id: string, data: Record<string, unknown>) => {
    const r = await api.patch(`/cards/nudge/${id}`, data);
    return r.data;
  },
  deleteNudge: async (id: string) => {
    const r = await api.delete(`/cards/nudge/${id}`);
    return r.data;
  },
  nudgeByEngine: async (engineType: number) => {
    const r = await api.get(`/cards/nudge/by-engine/${engineType}`);
    return r.data;
  },

  // Knowledge cards
  listKnowledge: async (params?: Record<string, unknown>) => {
    const r = await api.get('/cards/knowledge', { params });
    return r.data;
  },
  getKnowledge: async (id: string) => {
    const r = await api.get(`/cards/knowledge/${id}`);
    return r.data;
  },
  createKnowledge: async (data: Record<string, unknown>) => {
    const r = await api.post('/cards/knowledge', data);
    return r.data;
  },
  updateKnowledge: async (id: string, data: Record<string, unknown>) => {
    const r = await api.patch(`/cards/knowledge/${id}`, data);
    return r.data;
  },
  deleteKnowledge: async (id: string) => {
    const r = await api.delete(`/cards/knowledge/${id}`);
    return r.data;
  },
};

// ── Facilitator Marketplace API (Nino 2026-05-20) ──────────────────────
// Backs the FP "Active Skill Drills" tab. Hits /api/facilitator-marketplace
// on the same backend, with the same auth header pattern as schedulerApi.

// Consolidation A (2026-06-13) — shares the standard interceptors.
const rootApi: AxiosInstance = createScopedAxios('');

/** The server's switches (/health `features`), e.g. drill_observer once the observer seat is live. Public. */
export const serverFeaturesApi = {
  get: async (): Promise<Record<string, boolean>> => {
    const r = await rootApi.get('/health');
    return (r.data?.features ?? {}) as Record<string, boolean>;
  },
};

export interface ActiveDrillRow {
  workshop_id: string;
  title: string;
  pillars: string[];
  skills_assessed: string[];
  duration_min: number;
  difficulty: string;
  max_participants: number;
  min_participants: number;
  upcoming_sessions: number;
  bookings_in_upcoming: number;
  my_claimed_upcoming: number;
}

export interface HeatmapResponse {
  workshop_id: string;
  grid: number[][];
  signal_sources: string[];
}

export interface ClaimedSessionRow {
  session_id: string;
  workshop_id: string;
  workshop_title: string;
  scheduled_start: string;
  scheduled_end: string;
  duration_minutes: number;
  max_participants: number;
  confirmed_count: number;
  status: string;
}

export const facilitatorMarketplaceApi = {
  listActiveDrills: async (): Promise<ActiveDrillRow[]> => {
    const r = await rootApi.get('/api/facilitator-marketplace/active-drills');
    return r.data.drills || [];
  },
  getHeatmap: async (workshopId: string): Promise<HeatmapResponse> => {
    const r = await rootApi.get(
      `/api/facilitator-marketplace/active-drills/${workshopId}/demand-heatmap`,
    );
    return r.data;
  },
  claimSlot: async (
    workshopId: string,
    body: { start_time_utc: string; capacity_max?: number },
  ): Promise<ClaimedSessionRow> => {
    const r = await rootApi.post(
      `/api/facilitator-marketplace/active-drills/${workshopId}/claim`,
      body,
    );
    return r.data;
  },
  myClaimed: async (): Promise<ClaimedSessionRow[]> => {
    const r = await rootApi.get('/api/facilitator-marketplace/my-claimed');
    return r.data.sessions || [];
  },
  cancelClaim: async (sessionId: string) => {
    const r = await rootApi.delete(`/api/facilitator-marketplace/claimed/${sessionId}`);
    return r.data;
  },
};

// ── CC Facilitators (admin trust indicator) ──────────────────────────

export interface FacilitatorTrustRow {
  facilitator_id: string;
  display_name: string;
  email: string;
  status: string;
  sessions_delivered: number;
  upcoming_sessions: number;
  cancelled_by_facilitator: number;
  no_shows: number;
  avg_facilitator_rating: number | null;
  n_ratings: number;
  completion_rate: number;
  no_show_rate: number;
  trust_score: number;
  trust_band: 'excellent' | 'good' | 'developing' | 'watch' | 'at-risk';
}

export interface FacilitatorDetail {
  facilitator: FacilitatorTrustRow;
  recent_sessions: Array<{
    session_id: string;
    workshop_title: string;
    scheduled_start: string;
    status: string;
    confirmed_count: number;
    avg_rating: number | null;
  }>;
}

export const ccFacilitatorsApi = {
  list: async (): Promise<FacilitatorTrustRow[]> => {
    const r = await rootApi.get('/api/admin/cc/facilitators');
    return r.data.facilitators || [];
  },
  detail: async (facilitatorId: string): Promise<FacilitatorDetail> => {
    const r = await rootApi.get(`/api/admin/cc/facilitators/${facilitatorId}`);
    return r.data;
  },
  // C3 actions (Fable scorecard, 2026-06-13) — surfaced on the
  // Facilitators Leaderboard alongside the trust scores.
  suspend: async (facilitatorId: string, reason: string) => {
    const r = await rootApi.post(`/api/admin/cc/facilitators/${facilitatorId}/suspend`, { reason });
    return r.data;
  },
  unsuspend: async (facilitatorId: string, reason: string) => {
    const r = await rootApi.post(`/api/admin/cc/facilitators/${facilitatorId}/unsuspend`, { reason });
    return r.data;
  },
  addNote: async (facilitatorId: string, body: string) => {
    const r = await rootApi.post(`/api/admin/cc/facilitators/${facilitatorId}/notes`, { body });
    return r.data;
  },
  logOutreach: async (
    facilitatorId: string,
    payload: { channel: string; summary: string; follow_up_needed: boolean },
  ) => {
    const r = await rootApi.post(`/api/admin/cc/facilitators/${facilitatorId}/outreach`, payload);
    return r.data;
  },
  listActions: async (facilitatorId: string): Promise<FacilitatorActionRow[]> => {
    const r = await rootApi.get<{ actions: FacilitatorActionRow[] }>(
      `/api/admin/cc/facilitators/${facilitatorId}/actions`,
    );
    return r.data.actions || [];
  },
};

export interface FacilitatorActionRow {
  action_id: string;
  facilitator_id: string;
  action_type: 'suspend' | 'unsuspend' | 'note' | 'outreach';
  actor_admin_id: string;
  payload: Record<string, unknown>;
  created_at: string;
}

// ── Ops (CC-C1 phase 1b, Fable 2026-06-12) ────────────────────────
// Webhook event log + stuck-session detector for the new
// "Ops & incidents" tab.

export interface WebhookEventRow {
  event_id: string;
  source: string;
  event_type: string;
  external_id?: string | null;
  status: 'received' | 'processed' | 'failed' | 'replayed';
  error?: string | null;
  retry_count: number;
  received_at: string;
  processed_at?: string | null;
  replayed_at?: string | null;
}
export interface WebhookEventDetail extends WebhookEventRow {
  payload: Record<string, unknown>;
}
export interface StuckSessionRow {
  session_id: string;
  surface: 'ai_coach' | 'bridge';
  state: string;
  minutes_stuck: number;
  starts_at?: string | null;
  scenario_slug?: string | null;
  participant_count: number;
}
export interface UnlockPreview {
  session_id: string;
  surface: string;
  state: string;
  will_return_credits: boolean;
  credit_return_reason: string;
  affected_learner_count: number;
  notify_facilitator: boolean;
}

export const opsApi = {
  listWebhookEvents: async (params: { source?: string; status?: string; limit?: number; offset?: number }) => {
    const r = await rootApi.get<{ events: WebhookEventRow[]; total: number }>(
      '/api/admin/webhook-events',
      { params },
    );
    return r.data;
  },
  getWebhookEvent: async (eventId: string) => {
    const r = await rootApi.get<WebhookEventDetail>(`/api/admin/webhook-events/${eventId}`);
    return r.data;
  },
  replayWebhookEvent: async (eventId: string) => {
    const r = await rootApi.post<WebhookEventDetail>(`/api/admin/webhook-events/${eventId}/replay`);
    return r.data;
  },
  listStuckSessions: async (thresholdMinutes: number) => {
    const r = await rootApi.get<{ threshold_minutes: number; sessions: StuckSessionRow[] }>(
      '/api/admin/stuck-sessions',
      { params: { threshold_minutes: thresholdMinutes } },
    );
    return r.data;
  },
  previewUnlock: async (sessionId: string) => {
    const r = await rootApi.get<UnlockPreview>(`/api/admin/stuck-sessions/${sessionId}/preview`);
    return r.data;
  },
  unlockSession: async (sessionId: string, reason: string) => {
    const r = await rootApi.post(`/api/admin/stuck-sessions/${sessionId}/unlock`, { reason });
    return r.data;
  },
  // C1 phase 2 starter (2026-06-13) — audit-log read surface.
  listLearnerDeletions: async (params: { actor?: string; since?: string; limit?: number; offset?: number }) => {
    const r = await rootApi.get<{ rows: LearnerDeletionRow[]; total: number }>(
      '/api/admin/audit-log/learner-deletions',
      { params },
    );
    return r.data;
  },
  // Phase 2 expansion (2026-06-13).
  listFacilitatorActionsAudit: async (params: { actor?: string; action_type?: string; since?: string; limit?: number; offset?: number }) => {
    const r = await rootApi.get<{ rows: FacilitatorActionAuditRow[]; total: number }>(
      '/api/admin/audit-log/facilitator-actions',
      { params },
    );
    return r.data;
  },
  listTrustBandChanges: async (params: { facilitator_id?: string; since?: string; limit?: number; offset?: number }) => {
    const r = await rootApi.get<{ rows: TrustBandChangeRow[]; total: number }>(
      '/api/admin/audit-log/trust-band-changes',
      { params },
    );
    return r.data;
  },

  // 2026-06-14 (Nino "Ugly list" wave): unified admin_audit_events
  // surface. Filter by event_type ("workshop.deleted") or by
  // event_type_prefix ("theme"), entity_type/id, actor, or since.
  listAdminAuditEvents: async (params: {
    event_type?: string;
    event_type_prefix?: string;
    entity_type?: string;
    entity_id?: string;
    actor?: string;
    since?: string;
    limit?: number;
    offset?: number;
  }) => {
    const r = await rootApi.get<{ rows: AdminAuditEventRow[]; total: number }>(
      '/api/admin/audit-log/events',
      { params },
    );
    return r.data;
  },
  // 2026-07-02 (Pass 3 hardening — "Greats → Awesomes" dashboard).
  // Live snapshot of scoring engine invariant checks. Non-zero counts
  // = things worth investigating (orphan rows, out-of-range scores,
  // stuck sessions, zombie sandboxes).
  scoringIntegritySnapshot: async () => {
    const r = await rootApi.get<ScoringIntegritySnapshot>(
      '/api/admin/scoring-integrity/snapshot',
    );
    return r.data;
  },
};

export interface ScoringIntegritySnapshot {
  generated_at: string;
  total_violations: number;
  checks: {
    orphan_progression_rows: number | null;
    out_of_range_scores: number | null;
    cohort_without_org: number | null;
    duplicate_learner_emails: number | null;
    stuck_ai_coach_sessions: number | null;
    zombie_sandbox_tenants: number | null;
  };
  errored_checks: string[];
}

export interface AdminAuditEventRow {
  event_id: string;
  event_type: string;
  entity_type: string;
  entity_id?: string | null;
  actor_id: string;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface FacilitatorActionAuditRow {
  action_id: string;
  facilitator_id: string;
  action_type: 'suspend' | 'unsuspend' | 'note' | 'outreach';
  actor_admin_id: string;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface TrustBandChangeRow {
  audit_id: string;
  facilitator_id: string;
  old_band: string | null;
  new_band: string;
  trust_score: number;
  recorded_at: string;
}

export interface LearnerDeletionRow {
  audit_id: string;
  learner_id: string;
  display_name?: string | null;
  email?: string | null;
  deleted_at: string;
  deleted_by: string;
  reason?: string | null;
}

// ── CC Drill Request Queue (2026-08-20, Product [SPEC — CC request queue]) ─
//
// Frontend (Tools) owns the queue UI; backend (create/manage requests,
// state transitions, notification firing) is Scoring's.

import type { DrillQueueEntry } from '@/types/command-centre';

// Scoring's drill-request queue (src/api/drill_request_endpoints.py). The
// list was broken (doubled /api/v1 prefix, paged shape the server never
// sent). Schedule, availability by phone, can't-staff and the AI offer
// wait for Scoring's answers (Tools to Scoring 6 Oct, "Request queue,
// four contract questions"): the old calls used paths and fields the
// server refuses, so they're gone rather than left to fail.
/** One skill-drill price (GET /api/pricing/skill-drills): coach_type is ai_coach or live_coach. */
export interface SkillDrillSku { coach_type: string; duration_minutes: number; credit_cost: number }
export const pricingApi = {
  skillDrills: async (): Promise<SkillDrillSku[]> => {
    const r = await rootApi.get('/api/pricing/skill-drills');
    return (r.data?.skus ?? []) as SkillDrillSku[];
  },
};

/** Publishing an AI coach session (Scoring, 11 Oct): admin, refusals as 422 with the reason in words. */
export const aiCoachSlotsApi = {
  publish: async (body: { scenario_slug: string; starts_at: string; duration_minutes: 30 | 45; language: 'en' | 'id'; min_participants: number; max_participants: number }) => {
    const r = await rootApi.post('/api/ai-coach/slots', body);
    return r.data as { slot_id?: string; starts_at?: string };
  },
};

export const requestQueueApi = {
  list: async (): Promise<DrillQueueEntry[]> => {
    const response = await api.get('/drill-requests');
    return response.data;
  },
  // Whole-drill routes (Scoring, 7 Oct). Admin only.
  /** The learners waiting on a drill, oldest first. */
  waiting: async (classId: string): Promise<WaitingLearner[]> => {
    const response = await api.get(`/drill-requests/classes/${encodeURIComponent(classId)}/waiting`);
    return response.data;
  },
  /** Creates and publishes the session, closes every facilitator request on the drill and tells each learner. */
  schedule: async (classId: string, body: { facilitator_id: string; scheduled_at: string; duration_minutes: 30 | 45 | 60 | 90; max_participants: number; note?: string }): Promise<ScheduleDrillResult> => {
    const response = await api.post(`/drill-requests/classes/${encodeURIComponent(classId)}/schedule`, body);
    return response.data;
  },
  /** Closes every open request on the drill, with a note (learners aren't told yet: notification "not_wired"). */
  cannotStaff: async (classId: string, note: string): Promise<{ class_id: string; requests_closed: number; resolution_note: string; notification: string }> => {
    const response = await api.post(`/drill-requests/classes/${encodeURIComponent(classId)}/cannot-staff`, { note });
    return response.data;
  },
  /** Times a learner gave by phone: 1 to 20 windows, each with its zone. */
  setAvailability: async (requestId: string, windows: Array<{ starts_at: string; ends_at: string }>) => {
    const response = await api.post(`/drill-requests/${encodeURIComponent(requestId)}/availability`, { windows });
    return response.data;
  },
};

export interface WaitingLearner {
  request_id: string;
  learner_id: string;
  name: string | null;
  email: string | null;
  waiting_since: string | null;
  availability_given: boolean;
  availability: { windows?: Array<{ starts_at: string; ends_at: string }> } | null;
}

export interface ScheduleDrillResult {
  class_id: string;
  session_id: string;
  requests_closed: number;
  notification: 'sent' | 'partial' | 'failed' | 'no_learner_contact' | string;
  learners_notified: number;
  crowding?: unknown;
}

// ── Image Library (Nino approved 6 Oct; Scoring built the routes 6 Oct) ──
// Admin routes; the pictures themselves are public at /api/images/{slot}?v=N.
// Until the server update lands these 404, and the screen says so.
const imageLibraryAxios: AxiosInstance = createScopedAxios('/api/image-library');

export interface LibraryImage {
  slot: string;
  kind: 'class_icon' | 'scene' | 'drill_icon' | 'persona' | 'coach_face' | 'framework_icon';
  version: number;
  versions: number;
  content_type: string;
  size_bytes: number;
  uploaded_by: string;
  uploaded_at: string;
  retired: boolean;
  used_by: number;
  /** null for personas: they are kept for reference and never served. */
  url: string | null;
}

export interface LibraryList {
  images: LibraryImage[];
  active_face_set: 'robot' | 'drawn' | 'real';
}

export interface FaceSetState {
  active_face_set: 'robot' | 'drawn' | 'real';
  /** {set: [coaches with all 13 frames]} */
  complete_coaches: Record<string, string[]>;
  /** {set: {coach: [frames still missing]}} */
  missing: Record<string, Record<string, string[]>>;
}

export const imageLibraryApi = {
  list: async (): Promise<LibraryList> => {
    const r = await imageLibraryAxios.get('/images');
    return r.data;
  },
  /** Uploading to a slot that already holds an image is Replace (a new version). */
  upload: async (slot: string, kind: LibraryImage['kind'], file: Blob, fileName: string): Promise<LibraryImage> => {
    const form = new FormData();
    form.append('slot', slot);
    form.append('kind', kind);
    form.append('file', file, fileName);
    const r = await imageLibraryAxios.post('/images', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },
  /** Refused (409, "used by N") while anything shows the image. */
  retire: async (slot: string): Promise<{ slot: string; retired: boolean }> => {
    const r = await imageLibraryAxios.post(`/images/${encodeURIComponent(slot)}/retire`);
    return r.data;
  },
  faceSet: async (): Promise<FaceSetState> => {
    const r = await imageLibraryAxios.get('/face-set');
    return r.data;
  },
  /** Every AI room at once. Refused (409) until a coach in that set has all 13 frames. */
  setFaceSet: async (active_face_set: FaceSetState['active_face_set']): Promise<FaceSetState> => {
    const r = await imageLibraryAxios.put('/face-set', { active_face_set });
    return r.data;
  },
};

export default api;
